import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, CheckCircle, XCircle, BarChart3, Eye, EyeOff } from 'lucide-react';
import { getAdminPolls, addAdminPoll, updateAdminPoll, deleteAdminPoll, togglePollStatus } from '../services/api';

const ManagePolls = () => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPoll, setEditingPoll] = useState(null);
  
  const [formData, setFormData] = useState({
    id: null,
    question: '',
    status: 'active',
    visible: 1,
    options: [{ option_text: '' }, { option_text: '' }]
  });

  const loadPolls = async () => {
    setLoading(true);
    const data = await getAdminPolls();
    setPolls(data);
    setLoading(false);
  };

  useEffect(() => {
    loadPolls();
  }, []);

  const handleOptionChange = (idx, value) => {
    const newOptions = [...formData.options];
    newOptions[idx].option_text = value;
    setFormData({ ...formData, options: newOptions });
  };

  const addOptionField = () => {
    setFormData({ ...formData, options: [...formData.options, { option_text: '' }] });
  };

  const removeOptionField = (idx) => {
    const newOptions = formData.options.filter((_, i) => i !== idx);
    setFormData({ ...formData, options: newOptions });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.question.trim()) {
      alert("Question is required");
      return;
    }
    const cleanOptions = formData.options.filter(o => o.option_text.trim());
    if (cleanOptions.length < 2) {
      alert("At least 2 valid options are required");
      return;
    }

    const payload = { ...formData, options: cleanOptions };
    let success = false;
    
    try {
      if (formData.id) {
        const res = await updateAdminPoll(payload);
        success = res.success;
      } else {
        const res = await addAdminPoll(payload);
        success = res.success;
      }
    } catch(e) {
      console.error(e);
      success = false;
    }

    if (success) {
      setEditingPoll(null);
      setFormData({ id: null, question: '', status: 'active', visible: 1, options: [{ option_text: '' }, { option_text: '' }] });
      loadPolls();
    } else {
      alert("Failed to save poll");
    }
  };

  const handleEdit = (poll) => {
    setEditingPoll(true);
    setFormData({
      id: poll.id,
      question: poll.question,
      status: poll.status,
      visible: Number(poll.visible),
      options: poll.options.length ? poll.options.map(o => ({ ...o })) : [{ option_text: '' }, { option_text: '' }]
    });
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this poll? All votes will be lost forever.")) {
      const res = await deleteAdminPoll(id);
      if (res.success) loadPolls();
    }
  };

  const toggleVisibility = async (poll) => {
    const newVisible = poll.visible == 1 ? 0 : 1;
    await togglePollStatus({ id: poll.id, visible: newVisible });
    loadPolls();
  };
  
  const toggleStatus = async (poll) => {
    const newStatus = poll.status === 'active' ? 'inactive' : 'active';
    await togglePollStatus({ id: poll.id, status: newStatus });
    loadPolls();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-800">Manage Polls</h1>
          <p className="text-stone-500 mt-1">Create engaging questions for your visitors</p>
        </div>
        {!editingPoll && (
          <button 
            onClick={() => {
              setEditingPoll(true);
              setFormData({ id: null, question: '', status: 'active', visible: 1, options: [{ option_text: '' }, { option_text: '' }] });
            }}
            className="flex items-center px-4 py-2 bg-saffron-600 text-white rounded-lg hover:bg-saffron-700 transition"
          >
            <Plus className="w-5 h-5 mr-2" /> New Poll
          </button>
        )}
      </div>

      {editingPoll && (
        <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200 mb-8 shadow-sm">
          <h2 className="text-xl font-bold text-stone-800 mb-6 border-b border-stone-200 pb-4">
            {formData.id ? 'Edit Poll' : 'Create New Poll'}
          </h2>
          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-2">Poll Question</label>
              <input 
                type="text" 
                required 
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-saffron-500 text-lg"
                value={formData.question}
                onChange={(e) => setFormData({...formData, question: e.target.value})}
                placeholder="e.g. Which temple will you visit next?"
              />
            </div>

            <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm">
              <label className="block text-sm font-bold text-stone-700 mb-4">Poll Options</label>
              {formData.options.map((opt, idx) => (
                <div key={idx} className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 font-medium shrink-0">
                    {idx + 1}
                  </div>
                  <input
                    type="text"
                    required
                    className="flex-1 px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-saffron-500"
                    placeholder={`Option ${idx + 1}`}
                    value={opt.option_text}
                    onChange={(e) => handleOptionChange(idx, e.target.value)}
                  />
                  {formData.options.length > 2 && (
                    <button type="button" onClick={() => removeOptionField(idx)} className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <div className="mt-4">
                <button type="button" onClick={addOptionField} className="text-sm font-medium text-saffron-600 hover:text-saffron-700 flex items-center bg-saffron-50 px-3 py-1.5 rounded-lg transition">
                  <Plus className="w-4 h-4 mr-1" /> Add Option
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-6 border-t border-stone-200 pt-6">
              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  id="visible" 
                  checked={formData.visible === 1}
                  onChange={(e) => setFormData({...formData, visible: e.target.checked ? 1 : 0})}
                  className="w-5 h-5 text-saffron-600 rounded border-stone-300 focus:ring-saffron-500"
                />
                <label htmlFor="visible" className="font-medium text-stone-700">Visible on Frontend</label>
              </div>
              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  id="active" 
                  checked={formData.status === 'active'}
                  onChange={(e) => setFormData({...formData, status: e.target.checked ? 'active' : 'inactive'})}
                  className="w-5 h-5 text-saffron-600 rounded border-stone-300 focus:ring-saffron-500"
                />
                <label htmlFor="active" className="font-medium text-stone-700">Active (Accepting Votes)</label>
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button type="submit" className="px-6 py-2 bg-stone-900 text-white rounded-lg hover:bg-black transition font-medium">Save Poll</button>
              <button type="button" onClick={() => setEditingPoll(false)} className="px-6 py-2 border border-stone-300 text-stone-600 rounded-lg hover:bg-stone-50 transition font-medium">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-saffron-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {polls.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-stone-200">
              <BarChart3 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-xl text-stone-500 font-medium pb-4">No polls have been created yet.</p>
            </div>
          ) : (
            polls.map(poll => (
              <div key={poll.id} className={`bg-white rounded-xl border ${poll.visible == 1 ? 'border-stone-200' : 'border-stone-200 bg-stone-50 opacity-75'} p-5 shadow-sm hover:shadow transition flex flex-col md:flex-row md:items-center justify-between`}>
                <div className="mb-4 md:mb-0">
                  <h3 className="text-lg font-bold text-stone-800 mb-2">{poll.question}</h3>
                  <div className="flex flex-wrap gap-2 text-sm text-stone-500 mb-3">
                    {poll.options.map((opt, i) => (
                      <span key={i} className="bg-stone-100 px-2 py-1 rounded-md border border-stone-200">
                        {opt.option_text}: <strong className="text-stone-800 ml-1">{opt.votes}</strong>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-4 text-xs font-semibold">
                    <span onClick={() => toggleStatus(poll)} className={`cursor-pointer px-2 py-1 rounded ${poll.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {poll.status === 'active' ? '● ACTIVE' : '○ INACTIVE'}
                    </span>
                    <span onClick={() => toggleVisibility(poll)} className={`cursor-pointer flex items-center ${poll.visible == 1 ? 'text-blue-600' : 'text-stone-500'}`}>
                      {poll.visible == 1 ? <><Eye className="w-3.5 h-3.5 mr-1"/> Visible</> : <><EyeOff className="w-3.5 h-3.5 mr-1"/> Hidden</>}
                    </span>
                  </div>
                </div>
                
                <div className="flex space-x-2 border-t border-stone-100 pt-4 md:border-0 md:pt-0">
                  <button onClick={() => handleEdit(poll)} className="p-2 text-stone-500 hover:text-saffron-600 hover:bg-saffron-50 rounded-lg transition"><Edit2 className="w-5 h-5"/></button>
                  <button onClick={() => handleDelete(poll.id)} className="p-2 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"><Trash2 className="w-5 h-5"/></button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ManagePolls;
